import {
  IconPen,
  IconPlusRounded,
  IconTrash,
  IconUpload,
} from "@aksara-ui/icons";
import {
  Box,
  Button,
  Card,
  Circle,
  IconButton,
  InputText,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllCollectPoints,
  getAllTenantsFilter,
  importCollectPoint,
  setModalConfirmationOpen,
  setModalFormOpen,
} from "redux/actions";
import styled from "styled-components";
import {
  CollectPointConfirmationModal,
  CollectPointFormModal,
} from "./components";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import FilterKit from "components/FilterKit";
import { getRole } from "hooks";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const CollectPointsContent: React.FC = () => {
  const dispatch = useDispatch();
  const userRole = getRole();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const importFileExcelInput = useRef<any>(null);

  const { isLoading, isModalFormOpen, isModalConfirmationOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { collectPoints, isImportLoading } = useSelector(
    (state: any) => state.collectPointReducer
  );
  const { currentPage, totalPage, limitPage, lengthPage, filterTenantPage } =
    useSelector((state: any) => state.collectPointReducer);
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);

  const [selectedCollectPointId, setSelectedCollectPointId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllCollectPoints(
        1,
        event.target.value,
        limitPage,
        filterTenantPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllCollectPoints(currentPage, "", limitPage, filterTenantPage) as any
    );
  };

  const submitImportFileExcel = () => {
    importFileExcelInput.current!.click();
  };

  const handleChangeImportFileExcel = (event: any) => {
    const importFile = event.target.files[0];
    dispatch(importCollectPoint(importFile) as any);

    event.target.value = null;
  };

  const handleOpenFormModal = (collectPointId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedCollectPointId(collectPointId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedCollectPointId("");
  };

  const handleOpenConfirmModal = (collectPointId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedCollectPointId(collectPointId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedCollectPointId("");
  };

  useEffect(() => {
    dispatch(getAllTenantsFilter() as any);
    dispatch(getAllCollectPoints() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Collection Point
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <Box
            display={"flex"}
            flexDirection={isTablet ? "column" : "row"}
            justifyContent={"space-between"}
            style={{ gap: 5 }}
          >
            <InputSearchbox
              autoComplete="off"
              name="textDummy"
              placeholder="Search..."
              size={32}
              onChange={handleSearchChange}
              onClearButton={handleClearSearchChange}
            />
            {userRole === "superadmin" ? (
              <FilterKit
                orderByOptions={[
                  {
                    key: tenantsFilter,
                    label: "Tenant",
                    options: tenantsFilter.map((tenant: any) => {
                      return {
                        label: tenant.name,
                        value: tenant.id,
                      };
                    }),
                  },
                ]}
                onChangeOrder={(e: any) => {
                  dispatch(
                    getAllCollectPoints(
                      1,
                      "",
                      limitPage,
                      e[tenantsFilter] ? e[tenantsFilter].value : ""
                    ) as any
                  );
                }}
              />
            ) : (
              <></>
            )}
          </Box>

          <Stack
            display={"flex"}
            justifyContent={"right"}
            flexDirection={"row"}
            style={{ gap: 10 }}
          >
            <Box>
              <IconButton
                onClick={submitImportFileExcel}
                style={{
                  borderRadius: 10,
                  backgroundColor: `${theme.colors.grey02}`,
                  borderColor: `${theme.colors.grey04}`,
                }}
                type={"button"}
              >
                {isImportLoading ? (
                  <Circle />
                ) : (
                  <IconUpload fill={"currentColor"} />
                )}
              </IconButton>
              <InputText
                type="file"
                ref={importFileExcelInput}
                onChange={handleChangeImportFileExcel}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                style={{ display: "none" }}
              />
            </Box>

            <Button
              variant={"primary"}
              borderRadius={10}
              style={{ fontWeight: 600 }}
              icon={IconPlusRounded}
              iconPosition={"left"}
              size={32}
              onClick={() => handleOpenFormModal()}
            >
              Add Collection Point
            </Button>
          </Stack>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Code</TableHeadCell>
              <TableHeadCell>Point Reward</TableHeadCell>
              <TableHeadCell>Point XP</TableHeadCell>
              <TableHeadCell>Redeem at</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={5} />
            ) : !collectPoints.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={5} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              collectPoints.map((point: any) => {
                return (
                  <TableBodyRow key={point.id}>
                    <TableBodyCell>{point.code}</TableBodyCell>
                    <TableBodyCell>
                      {point.point_reward_collected}
                    </TableBodyCell>
                    <TableBodyCell>{point.point_xp_collected}</TableBodyCell>
                    <TableBodyCell>{point.redeemed_at ?? "-"}</TableBodyCell>
                    <TableBodyCell>
                      <IconButton onClick={() => handleOpenFormModal(point.id)}>
                        <IconPen fill={theme.colors.green06} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenConfirmModal(point.id)}
                      >
                        <IconTrash fill={theme.colors.red06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllCollectPoints(
                select,
                searchValue,
                limitPage,
                filterTenantPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllCollectPoints(
                  1,
                  searchValue,
                  selectedItem,
                  filterTenantPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <CollectPointFormModal
        collectPointId={selectedCollectPointId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <CollectPointConfirmationModal
        collectPointId={selectedCollectPointId}
        isOpen={isModalConfirmationOpen}
        onClose={() => handleCloseConfirmModal()}
      />
    </Box>
  );
};

export default CollectPointsContent;
